import { DirectorySort } from "@common/constants/counterparty";
import { ViewDensity, ViewType } from "@common/types/views/views";
import {
    CounterpartyDirectoryColumn,
    CounterpartyDirectoryView,
} from "@common/types/views/counterpartyDirectoryView";
import { ViewConfig } from "@lib/views";
import {
    AMOUNT_COLUMN_SIZE,
    CATEGORY_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";
import { CounterpartyNameCell } from "./cells/CounterpartyNameCell";
import { CounterpartySourceCell } from "./cells/CounterpartySourceCell";
import { CounterpartyAccountsCell } from "./cells/CounterpartyAccountsCell";
import { CounterpartyJournalEntriesCell } from "./cells/CounterpartyJournalEntriesCell";
import { CounterpartyAmountCell } from "./cells/CounterpartyAmountCell";
import { CounterpartyActionsCell } from "./cells/CounterpartyActionsCell";
import { CounterpartyEligible1099Cell } from "./cells/CounterpartyEligible1099Cell";
import { CounterpartyFederalTaxClassificationCell } from "./cells/CounterpartyFederalTaxClassificationCell";
import { CounterpartyBillableExpensesCell } from "./cells/CounterpartyBillableExpensesCell";
import { CounterpartyInvoicesCell } from "./cells/CounterpartyInvoicesCell";
import { CounterpartyBillsCell } from "./cells/CounterpartyBillsCell";

export function createCounterpartyDirectoryViewConfig(): ViewConfig<
    CounterpartyDirectoryViewRow,
    CounterpartyDirectoryColumn,
    DirectorySort
> {
    return {
        columns: {
            [CounterpartyDirectoryColumn.NAME]: {
                name: "Display name",
                header: "Display name",
                accessorKey: "name",
                cell: CounterpartyNameCell,
                minSize: 200,
                maxSize: 600,
                immutable: true,
                sort: {
                    asc: DirectorySort.NAME_ASC,
                    desc: DirectorySort.NAME_DESC,
                },
            },
            [CounterpartyDirectoryColumn.ACCOUNTS]: {
                name: "Accounts",
                header: "Accounts",
                accessorKey: "children",
                cell: CounterpartyAccountsCell,
                ...CATEGORY_COLUMN_SIZE,
                sort: {
                    asc: DirectorySort.ACCOUNTS_COUNT_ASC,
                    desc: DirectorySort.ACCOUNTS_COUNT_DESC,
                },
            },
            [CounterpartyDirectoryColumn.SOURCE]: {
                name: "Created by",
                header: "Created by",
                accessorKey: "counterparty",
                cell: CounterpartySourceCell,
                minSize: 80,
                maxSize: 160,
                sort: {
                    asc: DirectorySort.SOURCE_ASC,
                    desc: DirectorySort.SOURCE_DESC,
                },
            },
            [CounterpartyDirectoryColumn.JOURNAL_ENTRIES]: {
                name: "Journal entries",
                header: "Journal entries",
                accessorKey: "journalEntryLinesCount",
                cell: CounterpartyJournalEntriesCell,
                minSize: 100,
                maxSize: 160,
                sort: {
                    asc: DirectorySort.JOURNAL_ENTRY_LINES_ASC,
                    desc: DirectorySort.JOURNAL_ENTRY_LINES_DESC,
                },
            },
            [CounterpartyDirectoryColumn.DEBIT]: {
                name: "Debit",
                header: "Debit",
                accessorKey: "debit",
                cell: CounterpartyAmountCell,
                ...AMOUNT_COLUMN_SIZE,
                sort: {
                    asc: DirectorySort.DEBIT_ASC,
                    desc: DirectorySort.DEBIT_DESC,
                },
            },
            [CounterpartyDirectoryColumn.CREDIT]: {
                name: "Credit",
                header: "Credit",
                accessorKey: "credit",
                cell: CounterpartyAmountCell,
                ...AMOUNT_COLUMN_SIZE,
                sort: {
                    asc: DirectorySort.CREDIT_ASC,
                    desc: DirectorySort.CREDIT_DESC,
                },
            },
            [CounterpartyDirectoryColumn.AMOUNT]: {
                // Single-direction contexts (e.g. the 1099 task, which is
                // always Money Out) show one "Amount" column instead of
                // separate Debit/Credit columns, since Credit is always
                // empty there and just adds noise. Backed by the same
                // `debit` field as the Debit column.
                name: "Amount",
                header: "Amount",
                accessorKey: "debit",
                cell: CounterpartyAmountCell,
                ...AMOUNT_COLUMN_SIZE,
                // Only ever placed programmatically by the 1099 default view.
                // Hidden from Column Settings so the general Directory (which
                // still shows Debit/Credit) doesn't offer it as an addable
                // option that would duplicate the `debit`-backed Debit column.
                hiddenFromSettings: true,
                sort: {
                    asc: DirectorySort.DEBIT_ASC,
                    desc: DirectorySort.DEBIT_DESC,
                },
            },
            [CounterpartyDirectoryColumn.ELIGIBLE_1099]: {
                name: "1099 eligible",
                header: "1099 eligible",
                accessorKey: "counterparty",
                cell: CounterpartyEligible1099Cell,
                minSize: 96,
                maxSize: 120,
            },
            [CounterpartyDirectoryColumn.FEDERAL_TAX_CLASSIFICATION]: {
                name: "Federal tax classification",
                header: "Federal tax classification",
                accessorKey: "counterparty",
                cell: CounterpartyFederalTaxClassificationCell,
                minSize: 200,
                maxSize: 320,
            },
            [CounterpartyDirectoryColumn.BILLABLE_EXPENSES]: {
                // Hidden by default (omitted from the default view's column
                // list below); opt-in via column settings. Clicking the count
                // opens the transactions drawer filtered to this counterparty
                // as the billable customer.
                name: "Billable expenses",
                header: "Billable expenses",
                accessorKey: "billableExpenseCount",
                cell: CounterpartyBillableExpensesCell,
                minSize: 100,
                maxSize: 160,
            },
            [CounterpartyDirectoryColumn.INVOICES]: {
                // Hidden by default (omitted from the default view's column
                // list below); opt-in via column settings. Clicking the count
                // opens the invoices drawer filtered to this counterparty as
                // the customer.
                name: "Invoices",
                header: "Invoices",
                accessorKey: "invoiceCount",
                cell: CounterpartyInvoicesCell,
                minSize: 100,
                maxSize: 160,
            },
            [CounterpartyDirectoryColumn.BILLS]: {
                // Hidden by default (omitted from the default view's column
                // list below); opt-in via column settings. Clicking the count
                // opens the bills drawer filtered to this counterparty as the
                // vendor.
                name: "Bills",
                header: "Bills",
                accessorKey: "billCount",
                cell: CounterpartyBillsCell,
                minSize: 100,
                maxSize: 160,
            },
            [CounterpartyDirectoryColumn.ACTIONS]: {
                name: "Actions",
                header: "",
                accessorKey: "id",
                cell: CounterpartyActionsCell,
                minSize: 64,
                maxSize: 64,
                immutable: true,
                hiddenFromSettings: true,
            },
        },
        nesting: {
            enabled: true,
            expandColumn: CounterpartyDirectoryColumn.NAME,
            childrenKey: "children",
        },
    };
}

export function createDefaultCounterpartyDirectoryView(): CounterpartyDirectoryView {
    return {
        type: ViewType.COUNTERPARTY_DIRECTORY,
        columns: [
            {
                id: CounterpartyDirectoryColumn.NAME,
                options: { pin: "left" },
            },
            { id: CounterpartyDirectoryColumn.ACCOUNTS },
            { id: CounterpartyDirectoryColumn.SOURCE },
            { id: CounterpartyDirectoryColumn.JOURNAL_ENTRIES },
            { id: CounterpartyDirectoryColumn.DEBIT },
            { id: CounterpartyDirectoryColumn.CREDIT },
            { id: CounterpartyDirectoryColumn.ELIGIBLE_1099 },
            {
                id: CounterpartyDirectoryColumn.ACTIONS,
                options: { pin: "right" },
            },
        ],
        filters: {},
        options: {
            selection: true,
            bulkActions: true,
            density: ViewDensity.MD,
        },
    };
}
